import { contactName } from '@/clientOnlyUtils/clientUtils';
import { cn } from '@/clientOnlyUtils/cn';
import React from 'react';

import type { SvrContact } from '@poseidon/types/svr/svrContact.type';
import type { SvrOrg } from '@poseidon/types/svr/svrOrg.type';
import type { SvrWithTwilioFlags } from '@poseidon/types/svr/svrTeam.type';
import type { SvrUserTeamBase } from '@poseidon/types/svr/svrUserTeam.type';
import { Button } from '@poseidon/components/Button';
import { Communication, General, Media } from '@poseidon/components/StrokeIcons';
import { Tooltip } from '@poseidon/components/Tooltip';

import { ContactAvatar } from '@/components/Avatar';
import { api } from '@/utils/api';
import { useCallContext } from '~/components/Dialer/call.context';
import CallNotes from '~/components/Dialer/CallNotes/CallNotes';
import { SmallPadded } from '~/components/PaddedScrollable';
import styles from './Dialer.module.scss';
import { Draggable } from './Draggable/Draggable';

type Props = {
  dragElId?: string;
  userTeam: SvrUserTeamBase & SvrWithTwilioFlags;
  className?: string;
};
export const Dialer = ({ dragElId, userTeam, className }: Props) => {
  const [{ callOpen, callStarted, contact, formattedTime }, { startCall, hangUp, hideDialer, sendDigit }] =
    useCallContext();
  const contactQuery = api.contact.byId.useQuery({ contactId: contact?.id || '' }, { enabled: Boolean(contact?.id) });

  if (contact == null) return null;

  return (
    <div className={cn(styles.container, className)}>
      <div className={cn(styles.header, callStarted && styles.live)} id={dragElId}>
        <span className={styles.duration}>{callOpen && formattedTime}</span>

        <div className={styles.headerMid}>
          {callOpen && userTeam.twilioRecordCalls && (
            <Tooltip label="Recording">
              <span className={styles.circleIndicator}></span>
            </Tooltip>
          )}
          {callStarted && <span>Call in progress</span>}
        </div>

        <Button icon={<General.XMark />} text onClick={() => hideDialer()} />
      </div>
      <SmallPadded className={styles.body}>
        <div className={styles.content}>
          {contactQuery.data && (
            <Contact contact={contactQuery?.data?.contactV2} org={contactQuery.data.contactOrg ?? undefined} />
          )}

          <Keypad
            callStarted={callStarted}
            startCall={async () => {
              if (!contactQuery?.data?.contactV2) return;
              await startCall(contactQuery?.data?.contactV2);
            }}
            hangUp={hangUp}
            sendDigit={sendDigit}
          />
        </div>

        <CallNotes />
      </SmallPadded>
    </div>
  );
};

type KeyButtonProps = {
  text: string;
  extras?: string;
  onClick?: (digit: string) => void;
};
const KeyButton = ({ text, extras, onClick }: KeyButtonProps) => {
  return (
    <button className={styles.key} onClick={() => onClick?.(text)}>
      {text}
      {extras != null && <span>{extras}</span>}
    </button>
  );
};

type KeypadProps = {
  callStarted: boolean;
  startCall: () => void;
  hangUp: () => void;
  sendDigit: (digit: string) => void;
};
const Keypad = ({ callStarted, startCall, hangUp, sendDigit }: KeypadProps) => {
  return (
    <>
      <div className={styles.keys}>
        <KeyButton text="1" onClick={sendDigit} />
        <KeyButton text="2" extras="A B C" onClick={sendDigit} />
        <KeyButton text="3" extras="D E F" onClick={sendDigit} />
        <KeyButton text="4" extras="G H I" onClick={sendDigit} />
        <KeyButton text="5" extras="J K L" onClick={sendDigit} />
        <KeyButton text="6" extras="M N O" onClick={sendDigit} />
        <KeyButton text="7" extras="P Q R S" onClick={sendDigit} />
        <KeyButton text="8" extras="T U V" onClick={sendDigit} />
        <KeyButton text="9" extras="W X Y Z" onClick={sendDigit} />
        <KeyButton text="*" onClick={sendDigit} />
        <KeyButton text="0" onClick={sendDigit} />
        <KeyButton text="#" onClick={sendDigit} />
      </div>
      <div className={cn(styles.bottomKeys, 'grid gap-2 [grid-template-columns:repeat(3,48px)] place-items-center')}>
        <Button icon={<Media.MicrophoneOff01 />} text />
        {!callStarted && (
          <Button icon={<Communication.Phone />} type="succeed" onClick={startCall} className="rounded-full" />
        )}

        {callStarted && (
          <Button icon={<Communication.PhoneHangUp02 />} type="reset" onClick={hangUp} className="rounded-full" />
        )}

        <Button icon={<Media.Voicemail />} text />
      </div>
    </>
  );
};

type ContactProps = {
  contact: SvrContact;
  org?: SvrOrg;
};
const Contact = ({ contact, org }: ContactProps) => {
  const headline = contact.fields?.headline?.find((f) => f.primary)?.data;
  const name = contactName(contact);

  return (
    <div className={styles.contact}>
      <ContactAvatar contact={contact} org={org} shape="circle" className="size-11" />
      <div className={styles.contactInfo}>
        <div className={styles.name}>{name}</div>
        <div className={styles.headline}>{headline}</div>
      </div>
    </div>
  );
};

type DraggableDialerProps = {
  userTeam: SvrUserTeamBase & SvrWithTwilioFlags;
};

export const DraggableDialer = ({ userTeam }: DraggableDialerProps) => {
  const [{ contact }] = useCallContext();
  if (contact == null) return null;
  const dragElId = 'dialer-header';

  return (
    <Draggable id="dialer" dragElId={dragElId}>
      <Dialer userTeam={userTeam} dragElId={dragElId} />
    </Draggable>
  );
};
